import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { SAGE, TEXT } from '../theme/colors'
import { EASE_OUT, SPRING } from '../theme/motion'
import { useNavTree, useNavGroups } from './navItems'

const IDLE = 'rgba(195,200,191,0.4)'
const ACTIVE_BG = 'rgba(176,206,174,0.12)'
const HOVER_BG = 'rgba(53,53,52,0.55)'
// Radii as classes, never inline: framer rewrites inline radii during layout
// animations (mixing in the previous pill's corners), classes it leaves alone.
const PILL = 'rounded-full'
const TAB = 'rounded-r-full'

// Hover intent: a pointer sweeping past the screen edge shouldn't fling the
// rail open, and a brief overshoot past its edge shouldn't snap it shut.
const OPEN_DELAY = 70
const CLOSE_DELAY = 180

// Icon lands with a small pop when its page becomes active. Constants, so a
// re-render with the same state never replays the keyframes.
const POP = { scale: [1, 1.22, 1] }
const REST = { scale: 1 }

const labelStyle = { fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase' }

// Both pills animate position only: the rail widens through a CSS width
// transition, and a size morph measured mid-transition would stretch them.
// No crossfade: the old pill hides at once and the new one travels at full
// strength (framer's crossfade froze it near-invisible when the old pill sat
// inside a folding group).
function ActivePill({ shape, bar }) {
  return (
    <motion.span
      layoutId="nav-active"
      layout="position"
      layoutCrossfade={false}
      transition={{ layout: SPRING }}
      className={`absolute inset-0 pointer-events-none ${shape}`}
      style={{ background: ACTIVE_BG, borderLeft: `${bar}px solid ${SAGE}` }}
    />
  )
}

// Glides between rows instead of blinking on/off per row. Fades in only when
// the pointer arrives from outside the nav; row-to-row it is already there.
function HoverPill({ shape, fresh }) {
  return (
    <motion.span
      layoutId="nav-hover"
      layout="position"
      layoutCrossfade={false}
      initial={fresh ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ layout: SPRING, opacity: { duration: 0.18, ease: EASE_OUT } }}
      className={`absolute inset-0 pointer-events-none ${shape}`}
      style={{ background: HOVER_BG }}
    />
  )
}

// Children unfold top-down: guide line draws, rows follow in a short cascade.
// Folding reverses bottom-up and runs faster.
const groupVariants = {
  open: { transition: { staggerChildren: 0.035, delayChildren: 0.05 } },
  closed: { transition: { staggerChildren: 0.02, staggerDirection: -1 } },
}
const guideVariants = {
  open: { scaleY: 1, transition: { duration: 0.45, ease: EASE_OUT } },
  closed: { scaleY: 0, transition: { duration: 0.15 } },
}
const childVariants = {
  open: { opacity: 1, x: 0, transition: { ...SPRING, opacity: { duration: 0.25 } } },
  closed: { opacity: 0, x: -8, transition: { duration: 0.12 } },
}

export default function Sidebar({ activeNav, setActiveNav, fullscreen }) {
  const [expanded, setExpanded] = useState(false)
  const [hover, setHover] = useState({ id: null, fresh: true })
  const hovered = hover.id
  const hoverRow = (id) => setHover(h => (h.id === id ? h : { id, fresh: h.id === null }))
  const { openGroups, toggleGroup } = useNavGroups(activeNav)
  const navTree = useNavTree()
  const desktopNav = navTree.filter(i => !i.mobileOnly)
  const intent = useRef()

  const hoverRail = (open) => {
    clearTimeout(intent.current)
    intent.current = setTimeout(() => setExpanded(open), open ? OPEN_DELAY : CLOSE_DELAY)
  }
  useEffect(() => () => clearTimeout(intent.current), [])

  // Labels cascade in on open; on close everything leaves at once, quickly
  const labelMotion = (i) => ({
    animate: { opacity: expanded ? 1 : 0, x: expanded ? 0 : -6 },
    transition: expanded
      ? { delay: 0.06 + i * 0.022, duration: 0.32, ease: EASE_OUT }
      : { duration: 0.12 },
  })

  return (
    <motion.aside
      initial={{ x: -20, opacity: 0 }}
      animate={{ x: fullscreen ? '-100%' : 0, opacity: fullscreen ? 0 : 1 }}
      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      onMouseEnter={() => hoverRail(true)}
      onMouseLeave={() => hoverRail(false)}
      className="hidden md:flex fixed left-0 z-40 flex-col py-10"
      style={{
        pointerEvents: fullscreen ? 'none' : undefined,
        top: '76px',
        bottom: 0,
        left: 0,
        background: 'rgba(27, 28, 27, 0.9)',
        backdropFilter: 'blur(32px)',
        WebkitBackdropFilter: 'blur(32px)',
        borderRadius: '0 1.5rem 1.5rem 0',
        // Opened, the rail floats over the page — it earns a deeper shadow
        boxShadow: expanded ? '28px 0 80px rgba(0,0,0,0.65)' : '20px 0 60px rgba(0,0,0,0.5)',
        width: expanded ? '220px' : '56px',
        transition: expanded
          ? 'width 0.5s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.5s ease'
          : 'width 0.32s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.32s ease',
        overflow: 'hidden',
      }}
    >
      {/* Logo area — the mark stays still as the rail's anchor */}
      <div className="mb-10 flex items-center gap-3 overflow-hidden" style={{ minHeight: 44, paddingLeft: 10 }}>
        <div
          className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #b0ceae, #8ba889)' }}
        >
          <span
            className="material-symbols-outlined"
            style={{ color: '#1d361f', fontSize: '1.1rem', fontVariationSettings: "'FILL' 1" }}
          >
            home
          </span>
        </div>
        <div className="overflow-hidden whitespace-nowrap">
          <motion.div
            animate={{ opacity: expanded ? 1 : 0, x: expanded ? 0 : -10 }}
            transition={expanded ? { duration: 0.35, ease: EASE_OUT } : { duration: 0.12 }}
            className="font-headline font-bold text-lg"
            style={{ color: SAGE, letterSpacing: '-0.03em' }}
          >
            The Manor
          </motion.div>
          <motion.div
            animate={{ opacity: expanded ? 1 : 0, x: expanded ? 0 : -10 }}
            transition={expanded ? { delay: 0.05, duration: 0.35, ease: EASE_OUT } : { duration: 0.12 }}
            className="text-[9px] uppercase tracking-widest font-label text-outline"
          >
            All systems optimal
          </motion.div>
        </div>
      </div>

      {/* Nav Items — groups unfold only while the rail is expanded */}
      <motion.nav
        layoutScroll
        onMouseLeave={() => setHover({ id: null, fresh: true })}
        className="flex-1 min-h-0 flex flex-col gap-1 overflow-y-auto overflow-x-hidden no-scrollbar"
      >
        {desktopNav.map((item, index) => {
          const childActive = item.children?.some(c => c.id === activeNav) ?? false
          const unfolded = expanded && openGroups.has(item.id)
          const isPage = activeNav === item.id
          // Collapsed rail: the parent stands in for its active child
          const isActive = isPage || (childActive && !unfolded)
          const isAncestor = childActive && unfolded
          const isHovered = hovered === item.id
          const color = isActive || isAncestor ? SAGE : isHovered ? TEXT : IDLE
          return (
            // Rows reflow with FLIP when a group above them opens or closes
            <motion.div
              key={item.id}
              layout="position"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                layout: SPRING,
                default: { delay: 0.25 + index * 0.035, duration: 0.45, ease: EASE_OUT },
              }}
              className="relative"
            >
              <div className="relative" onMouseEnter={() => hoverRow(item.id)}>
                <motion.button
                  onClick={() => setActiveNav(item.id)}
                  whileTap={{ scale: 0.97 }}
                  className="relative w-full flex items-center gap-3 rounded-full text-left"
                  style={{ padding: '11px 18px 11px 18px', color, transition: 'color 0.2s ease' }}
                >
                  <AnimatePresence>{isHovered && <HoverPill shape={PILL} fresh={hover.fresh} />}</AnimatePresence>
                  {isActive && <ActivePill shape={PILL} bar={3} />}

                  <motion.span
                    initial={false}
                    animate={isPage ? POP : REST}
                    transition={{ duration: 0.45, ease: EASE_OUT }}
                    className="relative material-symbols-outlined flex-shrink-0"
                    style={{
                      fontSize: '1.25rem',
                      width: 20,
                      height: 20,
                      fontVariationSettings: isActive || isAncestor ? "'FILL' 1" : "'FILL' 0",
                      // Variable-font axis: the glyph fills in rather than swapping
                      transition: 'font-variation-settings 0.4s ease',
                    }}
                  >
                    {item.icon}
                  </motion.span>
                  <motion.span
                    {...labelMotion(index)}
                    className="relative whitespace-nowrap font-label font-semibold"
                    style={labelStyle}
                  >
                    {/* Hover nudge lives on the text, so the pills stay put */}
                    <motion.span
                      className="inline-block"
                      animate={{ x: isHovered && !isActive ? 3 : 0 }}
                      transition={SPRING}
                    >
                      {item.label}
                    </motion.span>
                  </motion.span>
                </motion.button>

                {item.children && (
                  <motion.button
                    onClick={() => toggleGroup(item.id)}
                    {...labelMotion(index)}
                    whileTap={{ scale: 0.85 }}
                    aria-label={`${item.label} ${openGroups.has(item.id) ? 'zuklappen' : 'aufklappen'}`}
                    aria-expanded={unfolded}
                    className="absolute w-7 h-7 rounded-full flex items-center justify-center text-outline-dim hover:text-on-surface hover:bg-surface-container-highest transition-colors"
                    style={{ top: '50%', marginTop: -14, right: 8, pointerEvents: expanded ? undefined : 'none' }}
                  >
                    <motion.span
                      className="material-symbols-outlined"
                      initial={false}
                      animate={{ rotate: openGroups.has(item.id) ? 180 : 0 }}
                      transition={SPRING}
                      style={{ fontSize: '1.1rem' }}
                    >
                      expand_more
                    </motion.span>
                  </motion.button>
                )}
              </div>

              {/* popLayout lifts the folding group out of flow at once, so
                  the rows below start gliding up while it fades */}
              <AnimatePresence initial={false} mode="popLayout">
                {item.children && unfolded && (
                  <motion.div
                    key="children"
                    variants={groupVariants}
                    initial="closed"
                    animate="open"
                    exit="closed"
                    className="relative my-1"
                    style={{ marginLeft: 27 }}
                  >
                    {/* Guide line under the parent icon's centre, drawn top-down */}
                    <motion.span
                      variants={guideVariants}
                      className="absolute left-0 top-0 bottom-0"
                      style={{ width: 1, background: 'rgba(195,200,191,0.12)', originY: 0 }}
                    />
                    <div className="flex flex-col gap-0.5">
                      {item.children.map((child) => {
                        const isChildActive = activeNav === child.id
                        const isChildHovered = hovered === child.id
                        return (
                          <motion.button
                            key={child.id}
                            variants={childVariants}
                            onClick={() => (child.href ? window.open(child.href, '_blank', 'noopener') : setActiveNav(child.id))}
                            onMouseEnter={() => hoverRow(child.id)}
                            whileTap={{ scale: 0.97 }}
                            className="relative flex items-center text-left whitespace-nowrap font-label font-semibold rounded-r-full"
                            style={{
                              ...labelStyle,
                              padding: '8px 16px 8px 23px',
                              color: isChildActive ? SAGE : isChildHovered ? TEXT : IDLE,
                              transition: 'color 0.2s ease',
                            }}
                          >
                            <AnimatePresence>{isChildHovered && <HoverPill shape={TAB} fresh={hover.fresh} />}</AnimatePresence>
                            {isChildActive && <ActivePill shape={TAB} bar={2} />}
                            <motion.span
                              className="relative inline-block"
                              animate={{ x: isChildHovered && !isChildActive ? 3 : 0 }}
                              transition={SPRING}
                            >
                              {child.label}
                              {child.href && <span className="material-symbols-outlined align-middle ml-1" style={{ fontSize: '0.8rem' }}>open_in_new</span>}
                            </motion.span>
                          </motion.button>
                        )
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )
        })}
      </motion.nav>

      {/* Emergency — one element that grows from a dot into a pill as the
          rail widens; the label unrolls beside the icon */}
      <div className="mt-6" style={{ paddingLeft: 10, paddingRight: 10 }}>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.94 }}
          transition={SPRING}
          className="w-full h-9 rounded-full flex items-center justify-center font-bold font-label whitespace-nowrap overflow-hidden"
          style={{
            background: 'rgba(111,55,34,0.8)',
            color: '#f1a286',
            fontSize: '9px',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
          }}
        >
          <span className="material-symbols-outlined flex-shrink-0" style={{ fontSize: '1rem' }}>power_settings_new</span>
          <AnimatePresence initial={false}>
            {expanded && (
              <motion.span
                initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                animate={{ opacity: 1, width: 'auto', marginLeft: 8 }}
                exit={{ opacity: 0, width: 0, marginLeft: 0, transition: { duration: 0.15 } }}
                transition={{ delay: 0.1, duration: 0.35, ease: EASE_OUT }}
                className="overflow-hidden"
              >
                Emergency Off
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </div>
  </motion.aside>
  )
}
